import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, X } from 'lucide-react';
import { recutShortlist, getNominationCounts, type CandidateHappening } from '@/lib/selections-api';
import type { CutoffRule } from '@littlebooker/dal';

interface RoundConfigLike {
  shortlist_happening_ids?: string[];
  cutoff_rule?: CutoffRule;
  manual_exclude?: string[];
  manual_include?: string[];
}

interface ShortlistEditorProps {
  selectionId: string;
  roundId: string;
  /** The nominate round's config (RoundConfig union); narrowed internally. */
  config: Record<string, unknown>;
  candidates: CandidateHappening[];
  targetShortlist: number;
  onChanged: () => void;
}

/**
 * Post-reveal manual trim: remove shows from the shortlist (or add one back) to
 * land on the target. Re-runs the stored cutoff rule with the owner's
 * include/exclude, so it's logged as a re-cut. Ballots are already revealed here,
 * so identities are fair game.
 */
export function ShortlistEditor({
  selectionId,
  roundId,
  config: rawConfig,
  candidates,
  targetShortlist,
  onChanged,
}: ShortlistEditorProps) {
  const config = rawConfig as RoundConfigLike;
  const base = config.shortlist_happening_ids ?? [];
  const [excluded, setExcluded] = useState<Set<string>>(new Set(config.manual_exclude ?? []));
  const [included, setIncluded] = useState<Set<string>>(new Set(config.manual_include ?? []));
  const [reason, setReason] = useState('');
  const [addId, setAddId] = useState('');
  const [error, setError] = useState('');

  const { data: tally } = useQuery({
    queryKey: ['selection-nominations', roundId],
    queryFn: () => getNominationCounts(selectionId, roundId),
  });
  const counts = tally?.counts ?? {};
  const wildcards = new Set(tally?.wildcardHappeningIds ?? []);
  const nomCount = (id: string) => counts[id] ?? 0;

  const title = (hid: string) => candidates.find((c) => c.id === hid)?.title ?? hid;

  // Shortlist as it will look: base minus excluded, plus manual includes.
  // Sorted least-nominated first so the trim candidates surface at the top.
  const shown = useMemo(() => {
    const ids = base.filter((id) => !excluded.has(id));
    for (const id of included) if (!ids.includes(id) && !excluded.has(id)) ids.push(id);
    return ids.sort((a, b) => (counts[a] ?? 0) - (counts[b] ?? 0) || title(a).localeCompare(title(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, excluded, included, tally]);

  const removedList = Array.from(excluded);
  const addable = candidates.filter((c) => !shown.includes(c.id));
  const overTarget = shown.length - targetShortlist;

  const dirty =
    JSON.stringify([...excluded].sort()) !== JSON.stringify([...(config.manual_exclude ?? [])].sort()) ||
    JSON.stringify([...included].sort()) !== JSON.stringify([...(config.manual_include ?? [])].sort());

  const apply = useMutation({
    mutationFn: () =>
      recutShortlist(selectionId, roundId, {
        rule: config.cutoff_rule ?? { type: 'top', k: base.length },
        exclude: Array.from(excluded),
        include: Array.from(included),
        reason: reason.trim() || null,
      }),
    onSuccess: onChanged,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to update shortlist'),
  });

  const remove = (id: string) => {
    setExcluded((p) => new Set(p).add(id));
    setIncluded((p) => {
      const n = new Set(p);
      n.delete(id);
      return n;
    });
  };
  const restore = (id: string) =>
    setExcluded((p) => {
      const n = new Set(p);
      n.delete(id);
      return n;
    });
  const add = (id: string) => {
    if (!id) return;
    setIncluded((p) => new Set(p).add(id));
    setExcluded((p) => {
      const n = new Set(p);
      n.delete(id);
      return n;
    });
    setAddId('');
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>Shortlist</CardDescription>
        <CardTitle className="text-base">
          {shown.length} shortlisted · target {targetShortlist}
          {overTarget > 0 && (
            <span className="ml-2 text-sm font-normal text-amber-600 dark:text-amber-500">
              {overTarget} over — remove {overTarget} to hit target
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Sorted fewest nominations first{tally ? ` · ${tally.ballotCount} ballots` : ''}. The left
          number is how many voters nominated the show; ★ = wildcard (guaranteed in).
        </p>
        <ul className="divide-y">
          {shown.map((id) => (
            <li key={id} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="w-7 text-right tabular-nums text-muted-foreground">{nomCount(id)}</span>
              <span className="font-medium">{title(id)}</span>
              {wildcards.has(id) && (
                <Star className="h-3.5 w-3.5 fill-current text-primary" aria-label="wildcard" />
              )}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 w-7 p-0 text-destructive hover:text-destructive"
                title="Remove from shortlist"
                onClick={() => remove(id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>

        {removedList.length > 0 && (
          <div className="rounded-md border bg-muted/20 p-2">
            <p className="text-xs font-medium text-muted-foreground">Removed</p>
            <ul className="mt-1 space-y-1">
              {removedList.map((id) => (
                <li key={id} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="line-through">{title(id)}</span>
                  <Button variant="ghost" size="sm" className="ml-auto h-6 px-2" onClick={() => restore(id)}>
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            value={addId}
            onChange={(e) => add(e.target.value)}
          >
            <option value="">+ Add a show…</option>
            {addable.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} ({nomCount(c.id)})
              </option>
            ))}
          </select>
        </div>

        {dirty && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Reason (logged)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button size="sm" onClick={() => apply.mutate()} disabled={apply.isPending}>
              {apply.isPending ? 'Applying…' : 'Apply changes'}
            </Button>
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
